<template>
  <div class="gs-finish-step">
    <section class="gs-finish-step__hero">
      <div class="gs-finish-step__badge">
        <check-circle-icon class="w-10 h-10 fill-current" />
      </div>
      <div>
        <p class="gs-finish-step__eyebrow">Product ready</p>
        <h2>{{$t('frontend_product_step11_congratulations')}}</h2>
        <p>{{ $t('frontend_product_step11_configured') }}</p>
      </div>
    </section>

    <section class="gs-finish-step__grid">
      <article class="gs-wizard-card gs-finish-step__panel gs-finish-step__panel--primary">
        <div class="gs-finish-step__panel-head">
          <div>
            <h3>{{$t('frontend_tracking')}} {{$t('frontend_link')}}</h3>
            <p>{{ localModel.type == 1 ? $t('frontend_product_step11_pastecode') : $t('frontend_product_step11_paste_this_code') }}</p>
          </div>
          <gs-button
            v-clipboard:copy="model.tracking_link"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
            variant="primary"
          >
            {{$t('frontend_copy')}}
          </gs-button>
        </div>
        <div class="gs-finish-step__copy-box">
          <text-input
            v-model="localModel.tracking_link"
            class="w-full"
            :readonly="true"
            value="localModel.tracking_link"
            :placeholder="$t('frontend_tracking_tracking_link')"
            :select-all="true"
          />
        </div>
      </article>

      <article class="gs-wizard-card gs-finish-step__panel">
        <div class="gs-finish-step__panel-head">
          <div>
            <h3>Launch checklist</h3>
            <p>Quick sanity check before this product starts taking traffic.</p>
          </div>
        </div>
        <div class="gs-finish-step__checklist">
          <div v-for="item in launchChecklist" :key="item.label" class="gs-finish-step__check-row" :class="{ 'is-complete': item.complete }">
            <span><gs-icon :name="item.complete ? 'check' : 'minus'" size="14"></gs-icon></span>
            <div>
              <strong>{{ item.label }}</strong>
              <small>{{ item.detail }}</small>
            </div>
          </div>
        </div>
      </article>

      <article class="gs-wizard-card gs-finish-step__panel">
        <div class="gs-finish-step__panel-head">
          <div>
            <h3>{{ $t('frontend_product_step10_proof_widget') }}</h3>
            <p>{{ proofWidgets.length ? 'Copy widget code for any proof widgets attached to this product.' : 'No proof widgets have been added to this product.' }}</p>
          </div>
        </div>
        <div v-if="proofWidgets.length > 0" class="gs-finish-step__widgets">
          <article v-for="(proofWidget, index) in proofWidgets" :key="proofWidget.id || index" class="gs-finish-step__widget">
            <div class="gs-finish-step__widget-head">
              <strong>{{ proofWidget.name }}</strong>
              <gs-button
                v-clipboard:copy="proofWidget.embed_code"
                v-clipboard:success="onCopyProofWidget"
                v-clipboard:error="onErrorProofWidget"
                variant="outline"
              >
                {{$t('frontend_copy')}}
              </gs-button>
            </div>
            <text-area
              v-model="proofWidget.embed_code"
              class="w-full"
              :readonly="true"
              value="proofWidget.embed_code"
              :placeholder="$t('frontend_widget_code')"
              :select-all="true"
            />
          </article>
        </div>
        <div v-else class="gs-finish-step__empty">
          Proof widgets are optional. You can come back and add one when you want social proof on the checkout experience.
        </div>
      </article>

      <article class="gs-wizard-card gs-finish-step__panel gs-finish-step__panel--actions">
        <div>
          <h3>{{$t('frontend_what_next')}}</h3>
          <p>Keep building promotion assets or return to the dashboard.</p>
        </div>
        <div class="gs-finish-step__next-actions">
          <gs-button variant="outline" @click="goToPromotionTools()">
            {{$t('frontend_create_promotion_tools')}}
          </gs-button>
          <gs-button variant="primary" @click="goToDashboard()">
            {{$t('frontend_back_to_dashboard')}}
          </gs-button>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import Notify from '@groovepages/gd-ui-kit/notify';
import CheckCircleIcon from '@/assets/images/icons/CheckCircleIcon.svg';

export default {
  name: 'Step11',
  components: {
    CheckCircleIcon,
  },
  props: {
    model: {
      required: true
    },
    product: {
      required: true
    }
  },
  data() {
    return {
      localModel: this.model,
      proofWidgets: [],
    };
  },
  validations: {
    localModel: {}
  },
  computed: {
    launchChecklist() {
      return [
        {
          label: 'Product information',
          detail: this.localModel.name || 'Product name is missing',
          complete: Boolean(this.localModel.name),
        },
        {
          label: 'Pricing',
          detail: `${(this.localModel.pricing_points || []).length} price point${(this.localModel.pricing_points || []).length === 1 ? '' : 's'} configured`,
          complete: (this.localModel.pricing_points || []).length > 0,
        },
        {
          label: 'Payment routing',
          detail: 'Gateways and checkout routing can be reviewed from the wizard.',
          complete: true,
        },
        {
          label: 'Tracking link',
          detail: this.localModel.tracking_link ? 'Ready to copy' : 'Tracking link not available yet',
          complete: Boolean(this.localModel.tracking_link),
        },
      ];
    },
  },
  async created() {
    this.$loader.start('fetchProofWidgets');
    this.proofWidgets = await this.product.proof_widgets().$get();
    this.$loader.end('fetchProofWidgets');
  },
  methods: {
    goToPromotionTools() {
      this.$router.push({
        name: 'funnel_resources',
        params: { id: this.localModel.funnel_id }
      });
    },

    goToDashboard() {
      this.$router.push({
        name: 'dashboard'
      });
    },

    onCopy() {
      Notify.success(this.$t('frontend_product_step11_success'));
    },

    onError() {
      Notify.fail(this.$t('frontend_unable_copy_to_clipboard'));
    },

    onCopyProofWidget() {
      Notify.success(this.$t('frontend_proof_widget_code_copy_to_clipboard'));
    },

    onErrorProofWidget() {
      Notify.fail(this.$t('frontend_unable_copy_proof_widget_code'));
    },

    getHex() {
      let rgb = window.getComputedStyle(document.documentElement).getPropertyValue(`--vs-${this.color}`);
      rgb = rgb.split(',');
      return `#${((1 << 24) + (Number(rgb[0]) << 16) + (Number(rgb[1]) << 8) + Number(rgb[2])).toString(16).slice(1)}`;
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    }
  }
};
</script>
<style>
.gs-finish-step {
  color: var(--gs-foreground);
}

.gs-finish-step__hero {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  padding: 28px 32px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-finish-step__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  color: var(--gs-success);
  background: var(--gs-success-soft);
  border: 1px solid var(--gs-success-soft);
  border-radius: 999px;
}

.gs-finish-step__eyebrow {
  margin: 0 0 6px;
  color: var(--gs-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.gs-finish-step h2,
.gs-finish-step h3 {
  margin: 0;
  color: var(--gs-foreground);
  line-height: 1.3;
}

.gs-finish-step h2 {
  font-size: 18px;
  font-weight: 700;
}

.gs-finish-step h3 {
  font-size: 15px;
  font-weight: 650;
}

.gs-finish-step p {
  margin: 8px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.5;
}

.gs-finish-step__grid {
  display: grid;
  gap: 14px;
  margin-top: 14px;
}


.gs-finish-step__panel-head,
.gs-finish-step__widget-head,
.gs-finish-step__panel--actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.gs-finish-step__copy-box {
  margin-top: 18px;
}

.gs-finish-step__checklist {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.gs-finish-step__check-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-finish-step__check-row > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--gs-muted-foreground);
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: 999px;
}

.gs-finish-step__check-row.is-complete > span {
  color: #ffffff;
  background: var(--gs-success);
  border-color: var(--gs-success);
}

.gs-finish-step__check-row strong,
.gs-finish-step__widget strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-finish-step__check-row small {
  display: block;
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.35;
}

.gs-finish-step__widgets {
  display: grid;
  gap: 12px;
  margin-top: 18px;
}

.gs-finish-step__widget {
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-finish-step__widget .w-full {
  margin-top: 12px;
}

.gs-finish-step__empty {
  margin-top: 18px;
  padding: 18px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-finish-step__next-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .gs-finish-step__hero,
  .gs-finish-step__panel-head,
  .gs-finish-step__panel--actions {
    grid-template-columns: 1fr;
    display: grid;
  }

  .gs-finish-step__next-actions {
    justify-content: flex-start;
  }
}
</style>
